import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Truck, Zap, Loader2, ShieldCheck } from "lucide-react";
import { formatPrice } from "@/lib/format";

const FREE_SHIPPING = 1500;
const PROMO_ENVIO_GRATIS = new Date() <= new Date("2026-11-30T23:59:59");

const inputCls =
  "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-amber-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100";

export const CheckoutModal = ({ open, onClose, cart, subtotal, giftWrap, api, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", address: "", city: "", zip: "", phone: "",
    delivery: "estandar",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [errors, setErrors] = useState({});

  const validators = {
    name: (v) => (v.trim().length >= 3 ? "" : "Escribe tu nombre completo"),
    email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? "" : "Correo no válido (ej. nombre@correo.com)"),
    address: (v) => (v.trim().length >= 8 ? "" : "Dirección incompleta (calle, número y colonia)"),
    city: (v) => (v.trim().length >= 3 ? "" : "Escribe tu ciudad"),
    zip: (v) => (/^\d{5}$/.test(v.trim()) ? "" : "El código postal debe tener 5 dígitos"),
    phone: (v) => (/^\d{10}$/.test(v.replace(/[\s-]/g, "")) ? "" : "El teléfono debe tener 10 dígitos"),
  };

  const validateStep1 = () => {
    const errs = {};
    for (const k of Object.keys(validators)) {
      const msg = validators[k](form[k]);
      if (msg) errs[k] = msg;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fieldCls = (k) =>
    `${inputCls} ${errors[k] ? "border-red-400 focus:border-red-500" : ""}`;

  const err = (k) =>
    errors[k] ? <p data-testid={`checkout-error-${k}`} className="-mt-1.5 px-1 text-[11px] text-red-500">{errors[k]}</p> : null;

  const giftCost = giftWrap ? 49 : 0;
  const shipping = form.delivery === "express" ? 299 : (PROMO_ENVIO_GRATIS || subtotal >= FREE_SHIPPING) ? 0 : 149;
  const total = subtotal + shipping + giftCost;

  const handlePay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${api}/checkout/preferences`, {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.qty })),
        customer: {
          name: form.name, email: form.email, address: form.address,
          city: form.city, zip: form.zip, phone: form.phone,
        },
        delivery: form.delivery,
        gift_wrap: giftWrap,
        shipping,
      });
      localStorage.setItem("hylasu-pending-order", data.order_id);
      window.location.assign(data.checkout_url);
    } catch {
      toast.error("No se pudo iniciar el pago con Mercado Pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  const optionCls = (active) =>
    `flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-all duration-300 ${
      active
        ? "border-amber-600 bg-amber-50 dark:border-amber-400 dark:bg-amber-500/10"
        : "border-stone-200 hover:border-stone-400 dark:border-stone-700 dark:hover:border-stone-500"
    }`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/60 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            data-testid="checkout-modal"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl dark:bg-stone-900"
          >
            <button onClick={onClose} data-testid="checkout-modal-close" aria-label="Cerrar checkout" className="absolute right-4 top-4 rounded-full p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900 dark:hover:bg-stone-800 dark:hover:text-white">
              <X size={18} />
            </button>

            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-700/80 dark:text-amber-400/90">
              Paso {step} de 3
            </p>
            <h2 className="mt-1 font-serif text-3xl font-light text-stone-900 dark:text-stone-50">
              {step === 1 && "Información de envío"}
              {step === 2 && "Método de entrega"}
              {step === 3 && "Pago seguro"}
            </h2>
            <div className="mt-4 flex gap-1.5">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${s <= step ? "bg-amber-600" : "bg-stone-200 dark:bg-stone-800"}`} />
              ))}
            </div>

            {step === 1 && (
              <div className="mt-6 space-y-3">
                <input data-testid="checkout-name-input" placeholder="Nombre completo" value={form.name} onChange={set("name")} className={fieldCls("name")} />
                {err("name")}
                <input data-testid="checkout-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={fieldCls("email")} />
                {err("email")}
                <input data-testid="checkout-address-input" placeholder="Dirección (calle, número, colonia)" value={form.address} onChange={set("address")} className={fieldCls("address")} />
                {err("address")}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input data-testid="checkout-city-input" placeholder="Ciudad" value={form.city} onChange={set("city")} className={fieldCls("city")} />
                    {err("city")}
                  </div>
                  <div>
                    <input data-testid="checkout-zip-input" placeholder="Código postal" inputMode="numeric" maxLength={5} value={form.zip} onChange={set("zip")} className={fieldCls("zip")} />
                    {err("zip")}
                  </div>
                </div>
                <input data-testid="checkout-phone-input" placeholder="Teléfono (10 dígitos)" inputMode="tel" value={form.phone} onChange={set("phone")} className={fieldCls("phone")} />
                {err("phone")}
                <button
                  data-testid="checkout-step-1-next"
                  onClick={() => { if (validateStep1()) setStep(2); }}
                  className="mt-2 w-full rounded-full bg-stone-900 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
                >
                  Continuar
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="mt-6 space-y-3">
                <button data-testid="delivery-option-estandar" onClick={() => setForm((f) => ({ ...f, delivery: "estandar" }))} className={optionCls(form.delivery === "estandar")}>
                  <Truck size={18} className="text-amber-600 dark:text-amber-400" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-stone-900 dark:text-stone-50">Envío Estándar</span>
                    <span className="block text-xs text-stone-500 dark:text-stone-400">3-5 días laborables</span>
                  </span>
                  <span className="text-sm font-semibold text-stone-900 dark:text-stone-50">
                    {PROMO_ENVIO_GRATIS || subtotal >= FREE_SHIPPING ? "Gratis" : formatPrice(149)}
                  </span>
                </button>
                <button data-testid="delivery-option-express" onClick={() => setForm((f) => ({ ...f, delivery: "express" }))} className={optionCls(form.delivery === "express")}>
                  <Zap size={18} className="text-amber-600 dark:text-amber-400" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-stone-900 dark:text-stone-50">Express Premium</span>
                    <span className="block text-xs text-stone-500 dark:text-stone-400">24-48h · mensajería dedicada</span>
                  </span>
                  <span className="text-sm font-semibold text-stone-900 dark:text-stone-50">{formatPrice(299)}</span>
                </button>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setStep(1)} className="flex-1 rounded-full border border-stone-200 py-3.5 text-sm font-medium text-stone-600 transition-colors hover:border-stone-400 dark:border-stone-700 dark:text-stone-300">
                    Atrás
                  </button>
                  <button
                    data-testid="checkout-step-2-next"
                    onClick={() => setStep(3)}
                    className="flex-1 rounded-full bg-stone-900 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl bg-stone-100 px-5 py-4 dark:bg-stone-800">
                  <div className="flex justify-between text-sm">
                    <span className="text-stone-600 dark:text-stone-300">Total a pagar</span>
                    <span data-testid="checkout-total" className="font-semibold text-stone-900 dark:text-stone-50">{formatPrice(total)}</span>
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-[11px] text-stone-500 dark:text-stone-400">
                    <ShieldCheck size={13} className="text-amber-600 dark:text-amber-400" />
                    Pago procesado de forma segura por Mercado Pago
                  </p>
                </div>
                <p className="text-center text-xs text-stone-500 dark:text-stone-400">
                  Tarjetas de crédito y débito · Transferencia · OXXO · Meses sin intereses
                </p>
                <div className="flex gap-3 pt-1">
                  <button onClick={() => setStep(2)} className="flex-1 rounded-full border border-stone-200 py-3.5 text-sm font-medium text-stone-600 transition-colors hover:border-stone-400 dark:border-stone-700 dark:text-stone-300">
                    Atrás
                  </button>
                  <button
                    data-testid="checkout-pay-submit-button"
                    onClick={handlePay}
                    disabled={loading}
                    className="flex flex-[1.4] items-center justify-center gap-2 rounded-full bg-[#009EE3] py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#007db5] disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : `Pagar con Mercado Pago`}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
